import type { AiQuotaThresholds } from '@a7y/types';

/**
 * Colour band for a quota bar, against the API-supplied admission policy.
 *
 * The bands are the policy's own states, not a taste gradient: below the
 * account's admission ceiling new automation work is admitted (emerald); at or
 * above it the account is refused for new work but still in the pool (amber);
 * at or above quarantine it leaves the pool entirely until it resets (red).
 * Codex's ceiling and quarantine coincide, so its bar has no amber band — which
 * is what its policy actually says.
 *
 * ABSENT THRESHOLDS RENDER NEUTRAL. Not a fallback literal: a hardcoded
 * boundary here would be a second admission regime wearing a different hat, and
 * it is how this component came to claim a 40% boundary for a 70% ceiling
 * (A7Y-2821). If the API did not state the policy, we do not know it, and a
 * grey bar says so honestly while the number beside it still reads.
 */
export function quotaBarClass(
  usedPercent: number,
  thresholds: AiQuotaThresholds | undefined
): string {
  if (!thresholds) return 'bg-zinc-400 dark:bg-zinc-500';
  if (usedPercent >= thresholds.quarantinePct) return 'bg-red-500';
  if (usedPercent >= thresholds.admissionCeilingPct) return 'bg-amber-500';
  return 'bg-emerald-500';
}

export function formatQuotaPercent(value: number): string {
  return `${Number.isInteger(value) ? value : value.toFixed(1)}%`;
}

/**
 * Whole minutes from `nowMs` until `iso`, rounded UP because the instant named
 * is still in the future.
 *
 * `null` means the timestamp could not be parsed, which is distinct from `0`
 * meaning it has passed — a caller must say "not reported" for the first rather
 * than render an interval it does not have.
 */
function minutesUntil(iso: string, nowMs: number): number | null {
  const deltaMs = Date.parse(iso) - nowMs;
  if (!Number.isFinite(deltaMs)) return null;
  return deltaMs <= 0 ? 0 : Math.ceil(deltaMs / 60_000);
}

/**
 * A span as its two largest non-zero units — `2d 4h`, `5h 23m`, `12m`.
 *
 * Two rather than three, and that is the entire reason this exists beside
 * `formatQuotaResetRemaining` rather than replacing it: this form goes in a
 * table column, where the third unit is noise (`in 0d 5h 23m` spends four
 * characters saying "not days"), while the detail page has the room for all
 * three and states the absolute time next to it.
 */
function formatSpanCompact(totalMinutes: number): string {
  const days = Math.floor(totalMinutes / 1_440);
  const hours = Math.floor((totalMinutes % 1_440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/** Fixed-width day/hour/minute countdown used beside provider reset times on
 * the detail page. A partial minute rounds up because the reset is still in the
 * future. */
export function formatQuotaResetRemaining(resetsAt: string, nowMs: number): string {
  const totalMinutes = minutesUntil(resetsAt, nowMs);
  if (totalMinutes == null || totalMinutes === 0) return 'reset due';
  const days = Math.floor(totalMinutes / 1_440);
  const hours = Math.floor((totalMinutes % 1_440) / 60);
  const minutes = totalMinutes % 60;
  return `in ${days}d ${hours}h ${minutes}m`;
}

/**
 * The reset countdown as a bare interval, for the account table's `Resets`
 * column — no absolute time and no `in` prefix, because the column header
 * already says what the number is.
 *
 * `null` when the provider reported no parseable reset instant; the caller
 * renders that as "not reported" rather than as a zero interval.
 */
export function formatQuotaResetInterval(resetsAt: string, nowMs: number): string | null {
  const totalMinutes = minutesUntil(resetsAt, nowMs);
  if (totalMinutes == null) return null;
  return totalMinutes === 0 ? 'due' : formatSpanCompact(totalMinutes);
}

/**
 * How long ago a reading was taken, as a bare interval — the account table's
 * `Updated` column.
 *
 * Rounded DOWN, the opposite of the countdown above and for the same reason: a
 * reading taken 50 seconds ago has been stale for zero whole minutes, and
 * rounding up would report a minute of staleness that has not yet elapsed.
 * Under a minute reads `just now` rather than `0m`, which also absorbs the
 * small negative span a clock skew can produce.
 */
export function formatQuotaAgeInterval(iso: string, nowMs: number): string | null {
  const deltaMs = nowMs - Date.parse(iso);
  if (!Number.isFinite(deltaMs)) return null;
  if (deltaMs < 60_000) return 'just now';
  return `${formatSpanCompact(Math.floor(deltaMs / 60_000))} ago`;
}
